import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Play,
  Pause,
  FileVideo,
  Clock,
  Plus,
  Tag,
  Trash2,
  Edit2,
  X,
  Check,
  Scissors,
  Zap,
  AlertTriangle,
  Sparkles,
  Maximize,
  ChevronDown,
} from 'lucide-react';
import { HeaderActions } from '@/components/layout/HeaderActions';
import DomRecordingPlayer from '@/components/content-library/DomRecordingPlayer';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import contentLibraryService, {
  VideoSegment,
  CreateSegmentInput,
  ActivateVideoSegment,
  GeneratedSegmentMetadata,
  GenerateMetadataResponse,
  PromptReviewCandidate,
  PromptReviewDecision,
  PromptReviewDecisionInput,
  BulkDeleteSegmentsResponse,
} from '@/lib/api/contentLibrary';
import SegmentReviewSection from '@/components/content-library/SegmentReviewSection';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KnowledgeRemovalDialog } from '@/components/onboarding/KnowledgeRemovalDialog';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import { useOrganizationMembers } from '@/hooks/useOrganizationsApi';

// Suggested content type values
const CONTENT_TYPES = [
  { value: 'demo', label: 'Demo' },
  { value: 'tutorial', label: 'Tutorial' },
  { value: 'overview', label: 'Overview' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'feature-highlight', label: 'Feature Highlight' },
];
const MAX_METADATA_INSTRUCTIONS_LENGTH = 2000;

const normalizePlaylistGroupValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '';
};

// Default reviewer decision for a pending candidate. Mirrors the mapping used
// by Assets staged review so behavior stays consistent between views.
const getDefaultDecisionForCandidate = (
  candidate: PromptReviewCandidate,
): PromptReviewDecision => {
  switch (candidate.recommended_action) {
    case 'possible_duplicate':
      return 'library_only';
    case 'possible_replacement':
      return candidate.matched_segment ? 'replace_matched' : 'approve';
    case 'library_only_candidate':
      return 'library_only';
    case 'new_candidate':
    default:
      return 'approve';
  }
};

const BULK_DECISION_LABELS: Record<PromptReviewDecision, string> = {
  approve: 'Add to catalog',
  replace_matched: 'Replace existing',
  library_only: 'Library only',
  ignore: 'Ignore',
};

// Tailwind color + tooltip for the small status dot rendered beside each
// segment in the sidebar. Returns null for legacy segments (no prompt_review
// metadata at all) so their row renders unchanged.
const getReviewStatusDotStyle = (
  status: string | undefined,
): { color: string; title: string } | null => {
  switch (status) {
    case 'review_pending':
      return { color: 'bg-amber-500', title: 'Pending review' };
    case 'prompt_approved':
      return { color: 'bg-emerald-500', title: 'In prompt' };
    case 'library_only':
      return { color: 'bg-zinc-400', title: 'Library only' };
    case 'ignored':
      return { color: 'bg-zinc-700 dark:bg-zinc-500', title: 'Ignored' };
    default:
      return null;
  }
};

interface SegmentFormData {
  label: string;
  description: string;
  tags: string[];
  ai_context: string;
  content_type: string;
}

interface CustomSegmentFormData {
  start_time: string;
  end_time: string;
  label: string;
}

interface SegmentTab {
  id: string;
  start_time: number;
  end_time: number;
  label: string;
}

const VideoViewerPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { interviewers } = useInterviewerSetup(orgId || '');
  const { members: orgMembers } = useOrganizationMembers(orgId || null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const domPlayerRef = useRef<{ goto: (timeInSeconds: number) => void } | null>(null);

  // UI State
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [metadataForm, setMetadataForm] = useState<SegmentFormData>({
    label: '',
    description: '',
    tags: [],
    ai_context: '',
    content_type: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [playlistPolicy, setPlaylistPolicy] = useState<'mixable' | 'standalone'>('mixable');
  const [playlistGroup, setPlaylistGroup] = useState('');
  const [isPlaylistControlsOpen, setIsPlaylistControlsOpen] = useState(false);

  // Custom segmentation state
  const [isCreateSegmentOpen, setIsCreateSegmentOpen] = useState(false);

  // Activation segments state (for pending_segmentation videos)
  const [activationSegments, setActivationSegments] = useState<CustomSegmentFormData[]>([
    { start_time: '', end_time: '', label: '' }
  ]);

  // AI metadata generation state
  const [autoGenerateMetadata, setAutoGenerateMetadata] = useState(false);
  const [createMetadataInstructions, setCreateMetadataInstructions] = useState('');
  const [metadataGenerationSegmentationId, setMetadataGenerationSegmentationId] = useState<string | null>(null);
  const [metadataGenerationInstructions, setMetadataGenerationInstructions] = useState('');
  const [showDeleteVideoDialog, setShowDeleteVideoDialog] = useState(false);
  const [showBulkDeleteSegmentsDialog, setShowBulkDeleteSegmentsDialog] = useState(false);
  const [showTrainingRemovalDialog, setShowTrainingRemovalDialog] = useState(false);
  // Segment deletion confirmation state
  const [segmentToDelete, setSegmentToDelete] = useState<{
    id: string;
    segmentation_id: string;
    label?: string;
  } | null>(null);
  // Timeline editor state - Tab-based multi-segment creation
  const [segmentTabs, setSegmentTabs] = useState<SegmentTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  
  // Derived state for active tab
  const activeTab = useMemo(() => 
    segmentTabs.find(tab => tab.id === activeTabId) || null
  , [segmentTabs, activeTabId]);
  
  // Compatibility layer: derive sliderRange from active tab
  const sliderRange: [number, number] = useMemo(() => 
    activeTab ? [activeTab.start_time, activeTab.end_time] : [0, 0]
  , [activeTab]);
  
  // Compatibility layer: derive segmentLabel from active tab
  const segmentLabel = activeTab?.label || '';
  
  // Helper to update active tab's slider range
  const setSliderRange = useCallback((range: [number, number]) => {
    if (!activeTabId) return;
    setSegmentTabs(tabs => tabs.map(tab => 
      tab.id === activeTabId 
        ? { ...tab, start_time: range[0], end_time: range[1] }
        : tab
    ));
  }, [activeTabId]);
  
  // Helper to update active tab's label
  const setSegmentLabel = useCallback((label: string) => {
    if (!activeTabId) return;
    setSegmentTabs(tabs => tabs.map(tab => 
      tab.id === activeTabId ? { ...tab, label } : tab
    ));
  }, [activeTabId]);
  
  // Helper to create a new segment tab
  const createSegmentTab = useCallback((duration: number) => {
    const id = `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newTab: SegmentTab = {
      id,
      start_time: 0,
      end_time: Math.min(30, duration),
      label: ''
    };
    setSegmentTabs(tabs => [...tabs, newTab]);
    setActiveTabId(id);
    return id;
  }, []);
  
  // Helper to remove a segment tab
  const removeSegmentTab = useCallback((tabId: string) => {
    setSegmentTabs(tabs => {
      const newTabs = tabs.filter(t => t.id !== tabId);
      // If we're removing the active tab, switch to another one
      if (activeTabId === tabId && newTabs.length > 0) {
        setActiveTabId(newTabs[newTabs.length - 1].id);
      }
      return newTabs;
    });
  }, [activeTabId]);

  const [computedVideoDuration, setComputedVideoDuration] = useState<number>(0);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [currentPreviewTime, setCurrentPreviewTime] = useState<number>(0);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
  const [generatedMetadataResults, setGeneratedMetadataResults] = useState<GenerateMetadataResponse | null>(null);
  const [isBulkApplyConfirmOpen, setIsBulkApplyConfirmOpen] = useState(false);

  // Fetch video details from organization videos
  const { data: videoData, isLoading: videoLoading, error: videoError } = useQuery({
    queryKey: ['video-details', videoId, orgId],
    queryFn: async () => {
      if (!videoId || !orgId) throw new Error('Video ID and Organization ID required');
      const response = await contentLibraryService.getOrganizationVideos(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      const video = response.data.videos.find((v) => v.video_id === videoId);
      if (!video) {
        throw new Error('Video not found');
      }
      return video;
    },
    enabled: !!videoId && !!orgId,
    retry: false,
  });

  // Fetch segmentations list
  const { data: segmentationsData, isLoading: segmentationsLoading, refetch: refetchSegmentations } = useQuery({
    queryKey: ['video-segmentations', videoId, orgId],
    queryFn: async () => {
      if (!videoId || !orgId) throw new Error('Video ID and Org ID required');
      const response = await contentLibraryService.listVideoSegmentations(videoId, orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch segmentations');
      }
      return response.data;
    },
    enabled: !!videoId && !!orgId,
    retry: false,
  });

  // Get raw segmentations list
  const rawSegmentationsList = segmentationsData?.segmentations || [];
  const segmentationIds = rawSegmentationsList.map(s => s.segmentation_id);

  // Fetch all segments with metadata using the unified endpoint
  const { data: allSegmentsResponse, isLoading: segmentsLoading, refetch: refetchSegments } = useQuery({
    queryKey: ['all-video-segments', videoId, orgId],
    queryFn: async () => {
      if (!videoId || !orgId) throw new Error('Video ID and Org ID required');
      const response = await contentLibraryService.getAllVideoSegments(videoId, orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch segments');
      }
      return response.data;
    },
    enabled: !!videoId && !!orgId,
    retry: false,
  });

  // Fetch prompt-review candidates (duplicate match data for pending segments).
  // Failure here degrades gracefully — the review section still renders using
  // the segment's own custom_metadata.prompt_review, minus match previews.
  const { data: promptReviewData, refetch: refetchPromptReview } = useQuery({
    queryKey: ['video-prompt-review', videoId, orgId],
    queryFn: async () => {
      if (!videoId || !orgId) throw new Error('Video ID and Org ID required');
      const response = await contentLibraryService.getVideoPromptReview(videoId, orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch prompt review candidates');
      }
      return response.data;
    },
    enabled: !!videoId && !!orgId,
    retry: false,
  });

  const candidatesBySegmentId = useMemo(() => {
    const map = new Map<string, PromptReviewCandidate>();
    (promptReviewData?.candidates || []).forEach((candidate) => {
      if (candidate.segment_id) {
        map.set(candidate.segment_id, candidate);
      }
    });
    return map;
  }, [promptReviewData]);

  // Candidates still awaiting a reviewer decision. Mirrors the filter used by
  // Assets staged review so the bulk action applies to the same set the user
  // sees in that flow.
  const pendingCandidates = useMemo<PromptReviewCandidate[]>(
    () =>
      (promptReviewData?.candidates || []).filter(
        (c) => c.review_status === 'review_pending',
      ),
    [promptReviewData],
  );

  // Breakdown of the default decision that will be applied to each pending
  // candidate. Surfaced in the confirmation dialog so reviewers see what they
  // are about to commit to before clicking through.
  const bulkDecisionBreakdown = useMemo(() => {
    const breakdown: Record<PromptReviewDecision, number> = {
      approve: 0,
      replace_matched: 0,
      library_only: 0,
      ignore: 0,
    };
    pendingCandidates.forEach((candidate) => {
      breakdown[getDefaultDecisionForCandidate(candidate)] += 1;
    });
    return breakdown;
  }, [pendingCandidates]);

  // Map user IDs (stored in prompt_review.reviewed_by) to display names so the
  // review section can show "Reviewed by Fazeel" instead of a raw UUID.
  const reviewerNameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    (orgMembers || []).forEach((member) => {
      if (!member.userId) return;
      const name = member.displayName || member.email;
      if (name) map.set(member.userId, name);
    });
    return map;
  }, [orgMembers]);

  const resolveReviewerName = useCallback(
    (userId: string): string | null => reviewerNameByUserId.get(userId) || null,
    [reviewerNameByUserId],
  );

  useEffect(() => {
    if (!videoData) return;
    setPlaylistPolicy(videoData.playlist_policy || 'mixable');
    setPlaylistGroup(normalizePlaylistGroupValue(videoData.playlist_group));
  }, [videoData]);

  const domRecordingUrl = videoData?.source_dom_recording_url || null;
  const domAudioUrl = videoData?.source_dom_audio_url || null;
  const isDomPlayback = !!domRecordingUrl;

  const handleDomPlayerReady = useCallback((api: { goto: (timeInSeconds: number) => void }) => {
    domPlayerRef.current = api;
  }, []);

  const seekToTime = useCallback((startTime: number) => {
    if (domRecordingUrl && domPlayerRef.current) {
      domPlayerRef.current.goto(startTime);
      return;
    }
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;
      // Don't auto-play, just seek to the timestamp
    }
  }, [domRecordingUrl]);

  // Segments already include metadata from the endpoint.
  // Sort auto segments first (matching sidebar order), then by start_time.
  // Spread into a new array to avoid mutating the React Query cache in place.
  const segmentsWithMetadata = useMemo(() => {
    if (!allSegmentsResponse?.segments) return [];
    return [...allSegmentsResponse.segments].sort((a, b) => {
      if (a.segment_type === 'auto' && b.segment_type !== 'auto') return -1;
      if (a.segment_type !== 'auto' && b.segment_type === 'auto') return 1;
      return a.start_time - b.start_time;
    });
  }, [allSegmentsResponse]);

  // Keep allSegmentsData for backwards compatibility with existing code
  const allSegmentsData = segmentsWithMetadata;

  // Auto-select a segment once segments load. Honors a `?segment=<id>` query
  // param (used when navigating from a cross-video duplicate-match link) when
  // that id exists in this video; otherwise falls back to the first segment.
  // Also seeks the player so the video starts at the selected segment's time.
  useEffect(() => {
    if (segmentsWithMetadata.length > 0 && !selectedSegmentId) {
      const paramId = searchParams.get('segment');
      const paramMatches = paramId
        ? segmentsWithMetadata.some((s) => s.id === paramId)
        : false;
      const targetId = paramMatches ? (paramId as string) : segmentsWithMetadata[0].id;
      setSelectedSegmentId(targetId);

      const target = segmentsWithMetadata.find((s) => s.id === targetId);
      if (target) {
        seekToTime(target.start_time);
      }
    }
  }, [segmentsWithMetadata, selectedSegmentId, searchParams, seekToTime]);

  // Derive selected segment from the segments array (so it updates when metadata loads)
  const selectedSegment = useMemo(() => {
    if (!selectedSegmentId) return null;
    return segmentsWithMetadata.find(seg => seg.id === selectedSegmentId) || null;
  }, [selectedSegmentId, segmentsWithMetadata]);

  // Build segmentationsList - derive segment_type from the segments we fetched
  const segmentationsList = useMemo(() => {
    const typeMap: Record<string, 'auto' | 'custom'> = {};
    (allSegmentsData || []).forEach(seg => {
      if (seg.segmentation_id && seg.segment_type) {
        // Map 'manual' from backend to 'custom' for frontend display/filtering
        typeMap[seg.segmentation_id] = seg.segment_type === 'manual' ? 'custom' : seg.segment_type;
      }
    });
    return rawSegmentationsList.map(seg => ({
      ...seg,
      segment_type: typeMap[seg.segmentation_id] || ('auto' as 'auto' | 'custom')
    }));
  }, [rawSegmentationsList, allSegmentsData]);

  // Track processing segmentations (must be before early returns)
  const processingSegmentationIds = useMemo(() => {
    const ids = new Set<string>();
    rawSegmentationsList.forEach(seg => {
      if (seg.status === 'processing') {
        ids.add(seg.segmentation_id);
      }
    });
    return ids;
  }, [rawSegmentationsList]);

  // Create custom segmentation mutation
  const createSegmentationMutation = useMutation({
    mutationFn: async ({
      segments,
      metadataInstructions,
    }: {
      segments: CreateSegmentInput[];
      metadataInstructions?: string;
    }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.createCustomSegmentation(videoId, {
        organization_id: orgId,
        segments,
        auto_generate_metadata: autoGenerateMetadata,
        metadata_instructions: autoGenerateMetadata ? metadataInstructions : undefined,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to create segmentation');
      }
      return response.data;
    },
    onSuccess: () => {
      const count = segmentTabs.length;
      if (autoGenerateMetadata) {
        toast.success(`${count > 1 ? `${count} segments` : 'Segment'} saved! AI metadata generating in background...`);
      } else {
        toast.success(`${count > 1 ? `${count} segments` : 'Segment'} saved! Processing thumbnails in background...`);
      }
      setIsCreateSegmentOpen(false);
      setAutoGenerateMetadata(false);
      setCreateMetadataInstructions('');
      // Tabs are reset by the dialog close effect
      refetchSegments();
      refetchSegmentations();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Update segment metadata mutation
  const updateMetadataMutation = useMutation({
    mutationFn: async ({ segmentationId, segmentId, data }: {
      segmentationId: string;
      segmentId: string;
      data: SegmentFormData
    }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.updateSegmentMetadata(
        videoId,
        segmentId,
        {
          organization_id: orgId,
          label: data.label || undefined,
          description: data.description || undefined,
          tags: data.tags.length > 0 ? data.tags : undefined,
          ai_context: data.ai_context || undefined,
          content_type: data.content_type || undefined,
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to update metadata');
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success('Segment metadata updated');
      setIsEditingMetadata(false);
      refetchSegments();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Delete segment metadata mutation
  const deleteMetadataMutation = useMutation({
    mutationFn: async ({ segmentationId, segmentId }: { segmentationId: string; segmentId: string }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.deleteSegmentMetadata(
        videoId,
        segmentationId,
        segmentId,
        orgId
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete metadata');
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success('Segment metadata cleared');
      refetchSegments();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Delete individual segment mutation
  const deleteSegmentMutation = useMutation({
    mutationFn: async ({ segmentationId, segmentId }: { segmentationId: string; segmentId: string }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.deleteSegment(
        videoId,
        segmentationId,
        segmentId,
        orgId
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete segment');
      }
      return { ...response.data, segmentId };
    },
    onSuccess: (data) => {
      toast.success('Segment deleted successfully');
      setSegmentToDelete(null);
      setSelectedSegmentId(null);
      
      // Optimistically remove segment from cache immediately
      queryClient.setQueryData(
        ['all-video-segments', videoId, orgId],
        (oldData: typeof allSegmentsResponse) => {
          if (!oldData?.segments) return oldData;
          return {
            ...oldData,
            segments: oldData.segments.filter((seg: VideoSegment) => seg.id !== data?.segmentId)
          };
        }
      );
      
      // Background refetch for consistency
      refetchSegments();
      refetchSegmentations();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Apply a single prompt-review decision for the selected segment
  const applyReviewMutation = useMutation({
    mutationFn: async ({ segmentId, decision }: { segmentId: string; decision: PromptReviewDecision }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      if (!segmentId) throw new Error('Missing segment ID');
      const response = await contentLibraryService.applyVideoPromptReview(videoId, {
        organization_id: orgId,
        decisions: [{ segment_id: segmentId, decision }],
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to apply review decision');
      }
      const failure = response.data.results?.find((r) => !r.success);
      if (failure) {
        throw new Error(failure.error || 'Failed to apply review decision');
      }
      return response.data;
    },
    onSuccess: (data) => {
      const result = data.results?.[0];
      const decisionCopy: Record<PromptReviewDecision, string> = {
        approve: 'Segment added',
        replace_matched: 'Duplicate replaced',
        library_only: 'Added to library only',
        ignore: 'Segment ignored',
      };
      const copy = result ? decisionCopy[result.decision] : 'Review updated';
      toast.success(copy);
      refetchSegments();
      refetchPromptReview();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Apply the default decision for every pending candidate on this video in a
  // single request. Mirrors the bulk-apply flow exposed by Assets staged
  // review and uses the same decision mapping.
  const applyAllPendingMutation = useMutation({
    mutationFn: async () => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      if (!pendingCandidates.length) {
        throw new Error('No pending segments to apply');
      }
      const decisions: PromptReviewDecisionInput[] = pendingCandidates.map((candidate) => ({
        segment_id: candidate.segment_id,
        decision: getDefaultDecisionForCandidate(candidate),
      }));
      const response = await contentLibraryService.applyVideoPromptReview(videoId, {
        organization_id: orgId,
        decisions,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to apply review decisions');
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (data.failed && data.failed > 0) {
        toast.warning(
          `Applied ${data.updated} segment${data.updated === 1 ? '' : 's'}, ${data.failed} failed`,
        );
      } else {
        toast.success(
          `Applied review to ${data.updated} segment${data.updated === 1 ? '' : 's'}`,
        );
      }
      setIsBulkApplyConfirmOpen(false);
      refetchSegments();
      refetchPromptReview();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Generate metadata mutation
  const generateMetadataMutation = useMutation({
    mutationFn: async ({
      segmentationId,
      metadataInstructions,
    }: {
      segmentationId: string;
      metadataInstructions?: string;
    }) => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      setIsGeneratingMetadata(true);
      const response = await contentLibraryService.generateMetadata(videoId, segmentationId, {
        organization_id: orgId,
        preview: false, // Always apply directly
        metadata_instructions: metadataInstructions,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to generate metadata');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setIsGeneratingMetadata(false);
      setMetadataGenerationSegmentationId(null);
      setMetadataGenerationInstructions('');
      // Show results modal with what was generated and applied
      setGeneratedMetadataResults(data);
      toast.success(`AI metadata applied to ${data.metadata_generated || 0} segments`);
      refetchSegments();

      // Show any errors that occurred
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(error => toast.error(error));
      }
    },
    onError: (error: Error) => {
      setIsGeneratingMetadata(false);
      toast.error(error.message);
    }
  });

  const generateVideoClipsMutation = useMutation({
    mutationFn: async () => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.generateVideoClips(videoId, {
        organization_id: orgId,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start clip generation');
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (!data.started) {
        toast.success(data.message);
        return;
      }

      const alreadyGeneratedSuffix = data.already_generated > 0
        ? ` ${data.already_generated} clip${data.already_generated === 1 ? '' : 's'} already existed.`
        : '';
      toast.success(`Queued ${data.clips_queued} clip asset${data.clips_queued === 1 ? '' : 's'} for background generation.${alreadyGeneratedSuffix}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Activate video mutation (for pending_segmentation videos)
  const activateVideoMutation = useMutation({
    mutationFn: async (segments: ActivateVideoSegment[]) => {
      if (!videoId) throw new Error('Missing video ID');
      const response = await contentLibraryService.activateVideo(videoId, { segments });
      if (!response.success) {
        throw new Error(response.error || 'Failed to activate video');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Video activated successfully');
      // Invalidate and refetch video data
      queryClient.invalidateQueries({ queryKey: ['video-details', videoId, orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
      // Reset activation segments
      setActivationSegments([{ start_time: '', end_time: '', label: '' }]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const updatePlaylistControlsMutation = useMutation({
    mutationFn: async () => {
      if (!videoId || !orgId) throw new Error('Missing IDs');
      const response = await contentLibraryService.updateVideoPlaylistControls(videoId, {
        organization_id: orgId,
        playlist_policy: playlistPolicy,
        playlist_group: normalizePlaylistGroupValue(playlistGroup) || null,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update playlist controls');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Playlist controls updated');
      setPlaylistPolicy(data.playlist_policy || 'mixable');
      setPlaylistGroup(normalizePlaylistGroupValue(data.playlist_group));
      queryClient.invalidateQueries({ queryKey: ['video-details', videoId, orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
      refetchSegments();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Bulk delete all segments mutation
  const bulkDeleteSegmentsMutation = useMutation({
    mutationFn: async () => {
      if (!videoId || !orgId) throw new Error('Missing video or organization ID');
      if (!rawSegmentationsList.length) throw new Error('No segmentations found for this video.');
      let totalDeleted = 0;
      for (const seg of rawSegmentationsList) {
        const response = await contentLibraryService.bulkDeleteSegments(
          videoId,
          seg.segmentation_id,
          orgId,
        );
        if (!response.success || !response.data) {
          throw new Error(response.error || `Failed to delete segments from segmentation ${seg.segmentation_id}.`);
        }
        totalDeleted += response.data.deleted_count;
      }
      return { deleted_count: totalDeleted };
    },
    onSuccess: (data) => {
      toast.success(`${data.deleted_count} segment(s) deleted. Catalog updated.`);
      setShowBulkDeleteSegmentsDialog(false);
      setSelectedSegmentId(null);
      refetchSegments();
      refetchSegmentations();
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setShowBulkDeleteSegmentsDialog(false);
    },
  });

  // Delete video mutation
  const deleteVideoMutation = useMutation({
    mutationFn: async () => {
      if (!videoId) throw new Error('Missing video ID');
      const response = await contentLibraryService.deleteVideo(videoId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete video');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Video deleted successfully');
      // Invalidate videos list and navigate back
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
      navigate('/content-library');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const attachedTrainingForVideo = useMemo(
    () =>
      interviewers.find((interviewer) => (interviewer.selected_video_ids || []).includes(videoId || '')) || null,
    [interviewers, videoId]
  );

  const videoUrl = useMemo(() => {
    if (videoData?.storage_url) return videoData.storage_url;
    return null;
  }, [videoData?.storage_url]);

  const videoDuration = useMemo(() => {
    // Use database duration first, then computed duration from video element
    return videoData?.duration_seconds || computedVideoDuration || 0;
  }, [videoData?.duration_seconds, computedVideoDuration]);

  const formatTime = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeInput = (value: string): number => {
    // Parse MM:SS or just seconds
    if (value.includes(':')) {
      const [mins, secs] = value.split(':').map(Number);
      return mins * 60 + secs;
    }
    return parseFloat(value) || 0;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    }
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const handleSegmentClick = (segment: VideoSegment) => {
    setSelectedSegmentId(segment.id);
    seekToTime(segment.start_time);
  };

  // Open a segment referenced from the duplicate-match banner. Same-video
  // matches just update local selection + seek; cross-video matches navigate
  // to the other video viewer with ?segment=<id> so that page auto-selects it.
  const handleOpenMatchedSegment = useCallback(
    (targetVideoId: string, targetSegmentId: string) => {
      if (!targetVideoId || !targetSegmentId) return;
      if (targetVideoId === videoId) {
        const match = segmentsWithMetadata.find((s) => s.id === targetSegmentId);
        if (match) {
          setSelectedSegmentId(match.id);
          seekToTime(match.start_time);
        }
        return;
      }
      navigate(
        `/content-library/video/${encodeURIComponent(targetVideoId)}?segment=${encodeURIComponent(targetSegmentId)}`,
      );
    },
    [videoId, segmentsWithMetadata, navigate, seekToTime],
  );

  const handleEditMetadata = async (segment: VideoSegment) => {
    // Refetch segments to ensure we have the latest metadata
    const result = await refetchSegments();

    // Find the segment from the refetched data
    let latestSegment = segment;
    if (result.data?.segments) {
      const foundSegment = result.data.segments.find(s => s.id === segment.id);
      if (foundSegment) {
        latestSegment = foundSegment;
      }
    }

    setMetadataForm({
      label: latestSegment.label || '',
      description: latestSegment.description || '',
      tags: latestSegment.tags || [],
      ai_context: latestSegment.ai_context || '',
      content_type: latestSegment.content_type || ''
    });
    setIsEditingMetadata(true);
  };

  const handleSaveMetadata = () => {
    if (!selectedSegment) return;
    updateMetadataMutation.mutate({
      segmentationId: selectedSegment.segmentation_id,
      segmentId: selectedSegment.id,
      data: metadataForm
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !metadataForm.tags.includes(tagInput.trim())) {
      setMetadataForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setMetadataForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const getCurrentVideoTime = () => {
    if (isDomPlayback) {
      return '—';
    }
    if (videoRef.current) {
      return formatTime(videoRef.current.currentTime);
    }
    return '0:00';
  };

  // Activation segment handlers (for pending_segmentation videos)
  const handleAddActivationSegmentRow = () => {
    setActivationSegments(prev => [...prev, { start_time: '', end_time: '', label: '' }]);
  };

  const handleRemoveActivationSegmentRow = (index: number) => {
    setActivationSegments(prev => prev.filter((_, i) => i !== index));
  };

  const handleActivationSegmentChange = (index: number, field: keyof CustomSegmentFormData, value: string) => {
    setActivationSegments(prev => prev.map((seg, i) =>
      i === index ? { ...seg, [field]: value } : seg
    ));
  };

  const setActivationSegmentTimeFromVideo = (index: number, field: 'start_time' | 'end_time') => {
    if (isDomPlayback) return;
    if (videoRef.current) {
      const time = formatTime(videoRef.current.currentTime);
      handleActivationSegmentChange(index, field, time);
    }
  };

  const handleActivateVideo = () => {
    const validSegments = activationSegments
      .filter(seg => seg.start_time && seg.end_time)
      .map(seg => ({
        start_time: formatTimeInput(seg.start_time),
        end_time: formatTimeInput(seg.end_time),
        label: seg.label || undefined,
      }));

    if (validSegments.length === 0) {
      toast.error('Please add at least one segment with start and end times');
      return;
    }

    activateVideoMutation.mutate(validSegments);
  };

  // Metadata generation handlers
  const handleGenerateMetadata = (segmentationId: string) => {
    setMetadataGenerationSegmentationId(segmentationId);
  };

  const handleSubmitGenerateMetadata = () => {
    if (!metadataGenerationSegmentationId) return;
    const trimmedInstructions = metadataGenerationInstructions.trim();
    generateMetadataMutation.mutate({
      segmentationId: metadataGenerationSegmentationId,
      metadataInstructions: trimmedInstructions || undefined,
    });
  };

  const handleCloseGenerateMetadataDialog = () => {
    if (generateMetadataMutation.isPending) return;
    setMetadataGenerationSegmentationId(null);
    setMetadataGenerationInstructions('');
  };

  const handleCloseResults = () => {
    setGeneratedMetadataResults(null);
  };

  // Initialize first segment tab when dialog opens and duration becomes available
  useEffect(() => {
    if (isCreateSegmentOpen && videoDuration > 0 && segmentTabs.length === 0) {
      createSegmentTab(videoDuration);
      setCurrentPreviewTime(0);
    }
  }, [isCreateSegmentOpen, videoDuration, segmentTabs.length, createSegmentTab]);

  // Track preview video playback state
  useEffect(() => {
    const video = previewVideoRef.current;
    if (!video || !isCreateSegmentOpen) return;

    const handleTimeUpdate = () => {
      setCurrentPreviewTime(video.currentTime);
    };

    const handlePlay = () => {
      setIsPreviewPlaying(true);
    };
    const handlePause = () => setIsPreviewPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [isCreateSegmentOpen]);

  // Reset preview state and tabs when dialog closes
  useEffect(() => {
    if (!isCreateSegmentOpen) {
      setIsPreviewPlaying(false);
      setCurrentPreviewTime(0);
      setSegmentTabs([]);
      setActiveTabId(null);
      setCreateMetadataInstructions('');
      if (previewVideoRef.current) {
        previewVideoRef.current.pause();
        previewVideoRef.current.currentTime = 0;
      }
    }
  }, [isCreateSegmentOpen]);

  // Check if video is in pending_segmentation status
  const isPendingSegmentation = videoData?.status === 'pending_segmentation';
  const hasPlaylistControlChanges = !!videoData && (
    playlistPolicy !== (videoData.playlist_policy || 'mixable') ||
    normalizePlaylistGroupValue(playlistGroup) !== normalizePlaylistGroupValue(videoData.playlist_group)
  );

  if (videoLoading || !currentOrganization) {
    return (
      <div className="flex flex-col border card-border rounded-xl card-bg h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading video...</p>
          </div>
        </div>
      </div>
    );
  }

  if (videoError || !videoData) {
    return (
      <div className="flex flex-col border card-border rounded-xl card-bg h-full">
        <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4 shrink-0">
          <div className="flex items-center gap-4">
            <HeaderActions />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load video: {videoError instanceof Error ? videoError.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Segments array with metadata merged in
  const segments = segmentsWithMetadata;

  // Group segments by segmentation_id for the manager view
  const segmentsBySegmentation: Record<string, any[]> = {};
  segments.forEach(seg => {
    if (!segmentsBySegmentation[seg.segmentation_id]) {
      segmentsBySegmentation[seg.segmentation_id] = [];
    }
    segmentsBySegmentation[seg.segmentation_id].push(seg);
  });

  // Add segment counts to segmentations list
  const segmentationsWithType = segmentationsList.map(seg => ({
    ...seg,
    segment_count: segmentsBySegmentation[seg.segmentation_id]?.length || seg.total_segments || 0
  }));

  return (
    <div className="flex min-h-0 flex-col h-full">
      {/* Top Header */}
      <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileVideo className="h-5 w-5 text-icon" />
            <span className="text-xl font-semibold text-subheading truncate">
              {videoData.title || videoData.filename}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {segments.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowBulkDeleteSegmentsDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All Segments
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (attachedTrainingForVideo) {
                  setShowTrainingRemovalDialog(true);
                } else {
                  setShowDeleteVideoDialog(true);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Video
            </Button>
            <HeaderActions />
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex-1 overflow-hidden px-4 sm:px-6 md:px-8 pb-4">
      <div className="flex min-h-0 h-full gap-4">
        {/* Left Sidebar — Segments */}
        <div className="w-[370px] shrink-0 rounded-xl border card-border bg-card flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b card-border flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold truncate">
                AI Segments
              </h3>
              <p className="text-xs text-muted-foreground">
                {segments.length} segment{segments.length !== 1 ? 's' : ''}
                {(segmentsLoading || segmentationsLoading) && (
                  <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />
                )}
              </p>
            </div>
            {!isPendingSegmentation && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2 shrink-0"
                onClick={() => setIsCreateSegmentOpen(true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Create
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {isPendingSegmentation ? (
              <div className="p-4 text-center">
                <AlertTriangle className="h-5 w-5 text-orange-500 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No segments yet. Define segments to activate this video.</p>
              </div>
            ) : segments.length > 0 ? (
              <div className="p-6 space-y-5">
                {segments.map((segment, idx) => {
                  const isProcessing = processingSegmentationIds.has(segment.segmentation_id || '');
                  const isSelected = selectedSegmentId === segment.id;
                  const reviewMeta = (segment.custom_metadata && typeof segment.custom_metadata === 'object')
                    ? (segment.custom_metadata as Record<string, unknown>).prompt_review
                    : null;
                  const reviewStatus = (reviewMeta && typeof reviewMeta === 'object')
                    ? (reviewMeta as { status?: string }).status
                    : undefined;
                  const statusDot = getReviewStatusDotStyle(reviewStatus);
                  return (
                    <button
                      key={segment.id}
                      onClick={() => !isProcessing && handleSegmentClick(segment)}
                      disabled={isProcessing}
                      className={`w-full text-left rounded-lg overflow-hidden transition-all ${
                        isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50'
                      } ${
                        isSelected
                          ? 'ring-2 ring-blue-600 bg-blue-50 dark:bg-blue-950/20'
                          : ''
                      }`}
                    >
                      <div className="flex gap-3 p-2">
                        {/* Thumbnail */}
                        <div className="relative w-[80px] shrink-0">
                          {segment.thumbnail_url ? (
                            <div className="aspect-video bg-zinc-800 rounded overflow-hidden">
                              <img
                                src={segment.thumbnail_url}
                                alt={segment.label || `Segment ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="aspect-video bg-zinc-800 rounded flex items-center justify-center">
                              <Play className="h-3 w-3 text-gray-500" />
                            </div>
                          )}
                          {isProcessing && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                              <Loader2 className="h-3 w-3 animate-spin text-white" />
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0 py-0.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {statusDot && (
                              <span
                                className={`h-1.5 w-1.5 rounded-full shrink-0 ${statusDot.color}`}
                                title={statusDot.title}
                                aria-label={statusDot.title}
                              />
                            )}
                            <p className="text-xs font-medium truncate">
                              {segment.label || `Segment ${idx + 1}`}
                            </p>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {formatTime(segment.start_time)} – {formatTime(segment.end_time)}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Badge
                              variant={segment.segment_type === 'auto' ? 'secondary' : 'default'}
                              className="text-[9px] px-1 py-0 h-3.5"
                            >
                              {segment.segment_type}
                            </Badge>
                            {segment.content_type && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">
                                {segment.content_type}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : !segmentsLoading && !segmentationsLoading ? (
              <div className="p-4 text-center">
                <p className="text-xs text-muted-foreground">No segments found.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 h-7 text-xs"
                  onClick={() => setIsCreateSegmentOpen(true)}
                >
                  <Scissors className="h-3 w-3 mr-1" />
                  Create Segments
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right Main Content */}
        <div className="flex-1 min-w-0 rounded-xl border card-border card-bg overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto">
          {/* Video Player */}
          <div className="flex-shrink-0">
            {domRecordingUrl ? (
              <div className="w-full" data-rrweb-container>
                <div className="rrweb-shell">
                  <DomRecordingPlayer
                    sourceUrl={domRecordingUrl}
                    audioUrl={domAudioUrl}
                    className="rounded-t-xl"
                    fillContainer
                    onPlayerReady={handleDomPlayerReady}
                    onDurationChange={(duration) => {
                      if (computedVideoDuration === 0) {
                        setComputedVideoDuration(duration);
                      }
                    }}
                  />
                </div>
              </div>
            ) : videoUrl ? (
              <video
                key={videoUrl}
                ref={videoRef}
                controls
                className="w-full max-h-[70vh] object-contain bg-black rounded-t-xl"
                src={videoUrl}
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  if (video.duration && isFinite(video.duration) && video.duration > 0 && computedVideoDuration === 0) {
                    setComputedVideoDuration(video.duration);
                  }
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full aspect-video max-h-[70vh] bg-zinc-900 flex items-center justify-center text-white">
                <div className="text-center">
                  <FileVideo className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Playback source not available</p>
                </div>
              </div>
            )}
          </div>

          {/* Pending Segmentation — Activation Flow */}
          {isPendingSegmentation ? (
            <div className="border-t card-border bg-card">
              <div className="px-4 py-3 border-b card-border bg-orange-50 dark:bg-orange-950/30">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <div>
                    <h3 className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                      Define Segments to Activate Video
                    </h3>
                    <p className="text-xs text-orange-600 dark:text-orange-400">
                      This video is waiting for you to define segments. Once activated, it will be added to your content library for search.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-subtle">
                    Define segments by specifying start and end times. Use MM:SS format or seconds.
                    Current playback time: <strong>{getCurrentVideoTime()}</strong>
                  </p>
                </div>

                {/* Activation Segment Rows */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {activationSegments.map((seg, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-subtle">Start Time</label>
                          <div className="flex gap-1">
                            <Input
                              value={seg.start_time}
                              onChange={(e) => handleActivationSegmentChange(index, 'start_time', e.target.value)}
                              placeholder="0:00"
                              className="text-sm"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActivationSegmentTimeFromVideo(index, 'start_time')}
                              title="Set from current video time"
                              disabled={isDomPlayback}
                            >
                              <Clock className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-subtle">End Time</label>
                          <div className="flex gap-1">
                            <Input
                              value={seg.end_time}
                              onChange={(e) => handleActivationSegmentChange(index, 'end_time', e.target.value)}
                              placeholder="0:30"
                              className="text-sm"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActivationSegmentTimeFromVideo(index, 'end_time')}
                              title="Set from current video time"
                              disabled={isDomPlayback}
                            >
                              <Clock className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-subtle">Label (optional)</label>
                          <Input
                            value={seg.label}
                            onChange={(e) => handleActivationSegmentChange(index, 'label', e.target.value)}
                            placeholder="Introduction"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      {activationSegments.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleRemoveActivationSegmentRow(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    onClick={handleAddActivationSegmentRow}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Segment
                  </Button>

                  <Button
                    onClick={handleActivateVideo}
                    disabled={activateVideoMutation.isPending}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    {activateVideoMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Activate Video
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Review Section — only renders for segments with prompt_review metadata */}
              {selectedSegment && (
                <SegmentReviewSection
                  key={selectedSegment.id}
                  segment={selectedSegment}
                  candidate={candidatesBySegmentId.get(selectedSegment.id) ?? null}
                  isApplying={applyReviewMutation.isPending}
                  onDecision={(decision) =>
                    applyReviewMutation.mutate({ segmentId: selectedSegment.id, decision })
                  }
                  resolveReviewerName={resolveReviewerName}
                  onOpenMatchedSegment={handleOpenMatchedSegment}
                />
              )}
              {/* Actions Bar */}
              <div className="px-4 py-3 border-t card-border bg-card flex items-center gap-2 flex-wrap">
                {pendingCandidates.length > 0 && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setIsBulkApplyConfirmOpen(true)}
                    disabled={applyAllPendingMutation.isPending}
                  >
                    {applyAllPendingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Apply all pending ({pendingCandidates.length})
                  </Button>
                )}
                {segmentationsWithType.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={isGeneratingMetadata}>
                        {isGeneratingMetadata ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-1 text-purple-500" />
                        )}
                        Generate Metadata
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {segmentationsWithType.map(seg => (
                        <DropdownMenuItem
                          key={seg.segmentation_id}
                          onClick={() => handleGenerateMetadata(seg.segmentation_id)}
                          disabled={isGeneratingMetadata || seg.status === 'processing'}
                        >
                          <Sparkles className="h-3 w-3 mr-2 text-purple-500" />
                          {seg.status === 'processing'
                            ? 'Processing...'
                            : `Generate for ${seg.segment_count || seg.total_segments} ${seg.segment_type} segments`}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generateVideoClipsMutation.mutate()}
                  disabled={generateVideoClipsMutation.isPending || segments.length === 0}
                >
                  {generateVideoClipsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <FileVideo className="h-4 w-4 mr-1" />
                  )}
                  Generate Clip Assets
                </Button>
                {selectedSegment && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditMetadata(selectedSegment)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Metadata
                  </Button>
                )}
                <div className="flex-1" />
                {selectedSegment && (
                  <div className="flex items-center gap-2">
                    {(selectedSegment.label || selectedSegment.description || (selectedSegment.tags && selectedSegment.tags.length > 0)) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => deleteMetadataMutation.mutate({
                          segmentationId: selectedSegment.segmentation_id,
                          segmentId: selectedSegment.id
                        })}
                        disabled={deleteMetadataMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Clear Metadata
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setSegmentToDelete({
                        id: selectedSegment.id,
                        segmentation_id: selectedSegment.segmentation_id,
                        label: selectedSegment.label
                      })}
                      disabled={deleteSegmentMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Segment
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Selected Segment Details — only for non-pending videos */}
          {selectedSegment && !isPendingSegmentation && (
            <Collapsible defaultOpen className="border-t card-border bg-card">
              <CollapsibleTrigger className="w-full px-4 py-3 border-b card-border flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors">
                <h3 className="text-sm font-semibold">Segment Details</h3>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform [[data-state=closed]>&]:rotate-[-90deg]" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="divide-y card-border">
                  {/* Name */}
                  <div className="flex items-start px-4 py-3">
                    <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Name</span>
                    <p className="text-sm font-medium">
                      {selectedSegment.label || 'Untitled Segment'}
                    </p>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-start px-4 py-3">
                    <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Timestamp</span>
                    <p className="text-sm">
                      {formatTime(selectedSegment.start_time)} - {formatTime(selectedSegment.end_time)}
                      <span className="mx-1">•</span>
                      Duration: {formatTime(selectedSegment.end_time - selectedSegment.start_time)}
                    </p>
                  </div>

                  {/* Description */}
                  <div className="flex items-start px-4 py-3">
                    <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Description</span>
                    <p className="text-sm">
                      {selectedSegment.description || <span className="text-muted-foreground italic">No description</span>}
                    </p>
                  </div>

                  {/* Content Type */}
                  {selectedSegment.content_type && (
                    <div className="flex items-start px-4 py-3">
                      <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Type</span>
                      <p className="text-sm">
                        {CONTENT_TYPES.find(t => t.value === selectedSegment.content_type)?.label || selectedSegment.content_type}
                      </p>
                    </div>
                  )}

                  {/* Tags */}
                  {selectedSegment.tags && selectedSegment.tags.length > 0 && (
                    <div className="flex items-start px-4 py-3">
                      <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Tags</span>
                      <div className="flex gap-1 flex-wrap">
                        {selectedSegment.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Context */}
                  {selectedSegment.ai_context && (
                    <div className="flex items-start px-4 py-3">
                      <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">AI Context</span>
                      <p className="text-sm">{selectedSegment.ai_context}</p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Metadata */}
          <Collapsible defaultOpen className="border-t card-border bg-card">
            <CollapsibleTrigger className="w-full px-4 py-3 border-b card-border flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors">
              <h3 className="text-sm font-semibold">Metadata</h3>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform [[data-state=closed]>&]:rotate-[-90deg]" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="divide-y card-border">
                <div className="flex items-start px-4 py-3">
                  <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Uploaded</span>
                  <p className="text-sm font-medium">
                    {new Date(videoData.created_at).toLocaleDateString()}
                  </p>
                </div>

                {videoData.duration_seconds && (
                  <div className="flex items-start px-4 py-3">
                    <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Duration</span>
                    <p className="text-sm font-medium">
                      {formatTime(videoData.duration_seconds)}
                    </p>
                  </div>
                )}

                {videoData.file_size_bytes && (
                  <div className="flex items-start px-4 py-3">
                    <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">File Size</span>
                    <p className="text-sm font-medium">
                      {formatFileSize(videoData.file_size_bytes)}
                    </p>
                  </div>
                )}

                <div className="flex items-start px-4 py-3">
                  <span className="text-sm text-muted-foreground w-[100px] shrink-0 pt-0.5">Format</span>
                  <p className="text-sm font-medium">
                    {videoData.mime_type || 'Unknown'}
                  </p>
                </div>
              </div>

            </CollapsibleContent>
          </Collapsible>

          {/* Playlist Controls */}
          <div className="border-t card-border bg-card px-4 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Playlist Controls</h3>
                  <Badge variant="outline">
                    {playlistPolicy === 'standalone' ? 'Standalone' : 'Mixable'}
                  </Badge>
                  {normalizePlaylistGroupValue(playlistGroup) && (
                    <Badge variant="secondary">{normalizePlaylistGroupValue(playlistGroup)}</Badge>
                  )}
                </div>
                <p className="text-sm text-subtle max-w-2xl">
                  These settings control whether this video can be combined with other uploads and which prompt lane it belongs to.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPlaylistControlsOpen(true)}
                >
                  Manage Controls
                </Button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
      </div>

      {/* Edit Metadata Dialog */}
      <Dialog open={isEditingMetadata} onOpenChange={setIsEditingMetadata}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Segment Metadata</DialogTitle>
            <DialogDescription>
              Add a label, description, and tags to this segment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Label</label>
              <Input
                value={metadataForm.label}
                onChange={(e) => setMetadataForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Introduction, Feature Demo"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={metadataForm.description}
                onChange={(e) => setMetadataForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what happens in this segment..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tags</label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button variant="outline" onClick={handleAddTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {metadataForm.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {metadataForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_type" className="text-sm font-medium">
                Content Type
                <span className="text-xs text-muted-foreground ml-2">(optional)</span>
              </Label>
              <Select
                value={metadataForm.content_type || undefined}
                onValueChange={(value) => setMetadataForm(prev => ({ ...prev, content_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select content type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Helps organize segments by category
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai_context" className="text-sm font-medium">
                AI Context
                <span className="text-xs text-muted-foreground ml-2">(optional)</span>
              </Label>
              <Textarea
                id="ai_context"
                value={metadataForm.ai_context}
                onChange={(e) => setMetadataForm(prev => ({ ...prev, ai_context: e.target.value }))}
                placeholder="e.g., Focus on the filtering capabilities, Mention this requires admin permissions"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Instructions for AI when referencing this segment
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditingMetadata(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMetadata}
              disabled={updateMetadataMutation.isPending}
            >
              {updateMetadataMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPlaylistControlsOpen}
        onOpenChange={(open) => {
          if (!updatePlaylistControlsMutation.isPending) {
            setIsPlaylistControlsOpen(open);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Playlist Controls</DialogTitle>
            <DialogDescription>
              Update how this video can be mixed with other uploads. Saving here also updates existing segments for this video and refreshes the prompt snapshot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="videoPlaylistPolicy">Mixing Policy</Label>
              <Select
                value={playlistPolicy}
                onValueChange={(value: 'mixable' | 'standalone') => setPlaylistPolicy(value)}
              >
                <SelectTrigger id="videoPlaylistPolicy">
                  <SelectValue placeholder="Select a policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixable">Mixable with matching videos</SelectItem>
                  <SelectItem value="standalone">Standalone only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-subtle">
                Standalone videos stay isolated even if another video shares the same prompt or customer account.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="videoPlaylistGroup">Playlist Group</Label>
              <Input
                id="videoPlaylistGroup"
                value={playlistGroup}
                onChange={(e) => setPlaylistGroup(e.target.value)}
                placeholder="mobile, desktop, enterprise"
              />
              <p className="text-xs text-subtle">
                Give videos the same group if they are allowed to mix together. Clear this field to remove the group.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPlaylistPolicy(videoData.playlist_policy || 'mixable');
                setPlaylistGroup(normalizePlaylistGroupValue(videoData.playlist_group));
                setIsPlaylistControlsOpen(false);
              }}
              disabled={updatePlaylistControlsMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPlaylistPolicy(videoData.playlist_policy || 'mixable');
                setPlaylistGroup(normalizePlaylistGroupValue(videoData.playlist_group));
              }}
              disabled={!hasPlaylistControlChanges || updatePlaylistControlsMutation.isPending}
            >
              Reset
            </Button>
            <Button
              onClick={() =>
                updatePlaylistControlsMutation.mutate(undefined, {
                  onSuccess: () => setIsPlaylistControlsOpen(false),
                })
              }
              disabled={!hasPlaylistControlChanges || updatePlaylistControlsMutation.isPending}
            >
              {updatePlaylistControlsMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Controls'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Custom Segments Timeline Editor Dialog */}
      <Dialog
        open={isCreateSegmentOpen}
        onOpenChange={(open) => {
          setIsCreateSegmentOpen(open);
          if (!open) {
            setAutoGenerateMetadata(false);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-5 w-5" />
              Create Custom Segments
            </DialogTitle>
            <DialogDescription>
              Use the timeline to select sections of the video. Add multiple segments using the + button.
            </DialogDescription>
          </DialogHeader>

          {/* Segment Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b">
            {segmentTabs.map((tab, index) => (
              <div
                key={tab.id}
                className={`group relative flex items-center gap-2 px-3 py-2 rounded-t-lg cursor-pointer transition-colors min-w-[120px] ${
                  activeTabId === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
                onClick={() => {
                  setActiveTabId(tab.id);
                  setCurrentPreviewTime(tab.start_time);
                  const video = previewVideoRef.current;
                  if (video) {
                    video.currentTime = tab.start_time;
                  }
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">
                    Segment {index + 1}
                  </div>
                  <div className={`text-[10px] truncate ${activeTabId === tab.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {formatTime(tab.start_time)} – {formatTime(tab.end_time)}
                  </div>
                </div>
                {segmentTabs.length > 1 && (
                  <button
                    className={`opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-black/10 ${
                      activeTabId === tab.id ? 'hover:bg-white/20' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSegmentTab(tab.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors shrink-0"
              onClick={() => createSegmentTab(videoDuration)}
              title="Add new segment"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {/* Video Preview (no native controls - controlled by timeline) */}
          {videoUrl && (
            <div className="bg-black rounded-lg overflow-hidden relative group">
              <video
                ref={previewVideoRef}
                src={videoUrl}
                className="w-full max-h-[40vh] min-h-[200px] object-contain"
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  if (video.duration && isFinite(video.duration) && video.duration > 0) {
                    setComputedVideoDuration(video.duration);
                    // Initialize slider range if not already set
                    if (sliderRange[0] === 0 && sliderRange[1] === 0) {
                      setSliderRange([0, Math.min(30, video.duration)]);
                    }
                  }
                }}
              />
              {/* Fullscreen button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8 bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => {
                  const video = previewVideoRef.current;
                  if (video) {
                    if (video.requestFullscreen) {
                      video.requestFullscreen();
                    } else if ((video as any).webkitRequestFullscreen) {
                      (video as any).webkitRequestFullscreen();
                    }
                  }
                }}
              >
                <Maximize className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* DOM Recording Preview (when no video URL available) */}
          {!videoUrl && isDomPlayback && domRecordingUrl && (
            <div className="bg-black rounded-lg overflow-hidden max-h-[40vh] min-h-[200px]">
              <DomRecordingPlayer
                sourceUrl={domRecordingUrl}
                audioUrl={domAudioUrl}
                className="w-full"
                onDurationChange={(duration) => {
                  if (computedVideoDuration === 0) {
                    setComputedVideoDuration(duration);
                    // Initialize slider range if not already set
                    if (sliderRange[0] === 0 && sliderRange[1] === 0) {
                      setSliderRange([0, Math.min(30, duration)]);
                    }
                  }
                }}
              />
            </div>
          )}

          {/* Timeline Controls & Slider */}
          <div className="space-y-3 px-1">
            {/* Playback controls row */}
            {videoUrl && videoDuration > 0 && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => {
                    const video = previewVideoRef.current;
                    if (!video) return;
                    if (isPreviewPlaying) {
                      video.pause();
                    } else {
                      // Seek to start of range before playing
                      video.currentTime = sliderRange[0];
                      setCurrentPreviewTime(sliderRange[0]);
                      video.play();
                    }
                  }}
                >
                  {isPreviewPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </Button>
                <div className="text-sm text-muted-foreground">
                  Preview selected segment ({formatTime(sliderRange[0])} – {formatTime(sliderRange[1])})
                </div>
              </div>
            )}

            <Label className="text-sm font-medium">Select Time Range</Label>
            {videoDuration > 0 ? (
              <>
                {/* Slider with playback position marker */}
                <div className="relative">
                  <Slider
                    min={0}
                    max={videoDuration}
                    step={0.5}
                    value={sliderRange}
                    onValueChange={(val: number[]) => {
                      setSliderRange([val[0], val[1]]);
                      // Pause and reset preview when range changes
                      const video = previewVideoRef.current;
                      if (video && isPreviewPlaying) {
                        video.pause();
                        video.currentTime = val[0];
                        setCurrentPreviewTime(val[0]);
                      }
                    }}
                    className="py-2"
                  />
                  {/* Playback position indicator - always visible, draggable across full video */}
                  <div
                    className="absolute top-0 bottom-0 w-4 z-10 cursor-pointer flex justify-center"
                    style={{
                      left: `${(currentPreviewTime / videoDuration) * 100}%`,
                      transform: 'translateX(-50%)',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const container = e.currentTarget.parentElement;
                      if (!container) return;

                      const handleDrag = (moveEvent: MouseEvent) => {
                        const rect = container.getBoundingClientRect();
                        const x = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
                        const percent = x / rect.width;
                        const newTime = Math.max(0, Math.min(percent * videoDuration, videoDuration));
                        const video = previewVideoRef.current;
                        if (video) {
                          video.currentTime = newTime;
                          setCurrentPreviewTime(newTime);
                        }
                      };

                      const handleUp = () => {
                        document.removeEventListener('mousemove', handleDrag);
                        document.removeEventListener('mouseup', handleUp);
                      };

                      document.addEventListener('mousemove', handleDrag);
                      document.addEventListener('mouseup', handleUp);
                    }}
                  >
                    <div className="w-0.5 h-full bg-white" />
                    {/* Playhead indicator */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-sm hover:scale-110 transition-transform" />
                  </div>
                </div>
                {/* Current time indicator */}
                <div className="text-xs text-center text-muted-foreground -mt-1">
                  Current: <strong className="text-foreground">{formatTime(currentPreviewTime)}</strong>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <button
                    type="button"
                    className="hover:text-foreground transition-colors"
                    title="Click to set start to current playhead position"
                    onClick={() => {
                      if (currentPreviewTime < sliderRange[1] - 1) {
                        setSliderRange([currentPreviewTime, sliderRange[1]]);
                      }
                    }}
                  >
                    Start: <strong>{formatTime(sliderRange[0])}</strong>
                  </button>
                  <span>
                    Duration: <strong>{formatTime(sliderRange[1] - sliderRange[0])}</strong>
                  </span>
                  <button
                    type="button"
                    className="hover:text-foreground transition-colors"
                    title="Click to set end to current playhead position"
                    onClick={() => {
                      if (currentPreviewTime > sliderRange[0] + 1) {
                        setSliderRange([sliderRange[0], currentPreviewTime]);
                      }
                    }}
                  >
                    End: <strong>{formatTime(sliderRange[1])}</strong>
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Video duration not available. Enter times manually:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-subtle">Start Time (MM:SS or seconds)</label>
                    <Input
                      value={sliderRange[0] > 0 ? formatTime(sliderRange[0]) : ''}
                      onChange={(e) => setSliderRange([formatTimeInput(e.target.value), sliderRange[1]])}
                      placeholder="0:00"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-subtle">End Time (MM:SS or seconds)</label>
                    <Input
                      value={sliderRange[1] > 0 ? formatTime(sliderRange[1]) : ''}
                      onChange={(e) => setSliderRange([sliderRange[0], formatTimeInput(e.target.value)])}
                      placeholder="0:30"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Label Input */}
          <div className="space-y-2 px-1">
            <Label className="text-sm font-medium">
              Label <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Input
              value={segmentLabel}
              onChange={(e) => setSegmentLabel(e.target.value)}
              placeholder="e.g., Feature Demo, Settings Overview"
            />
          </div>

          {/* Auto-generate metadata toggle */}
          <div className="flex items-center justify-between px-1 py-3 border-t border-b">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <div>
                <Label htmlFor="auto-generate-edit" className="text-sm font-medium cursor-pointer">
                  Auto-generate metadata
                </Label>
                <p className="text-xs text-muted-foreground">
                  Auto-generate descriptions, tags, and context (takes 30-60 seconds)
                </p>
              </div>
            </div>
            <Switch
              id="auto-generate-edit"
              checked={autoGenerateMetadata}
              onCheckedChange={(checked) => {
                setAutoGenerateMetadata(checked);
                if (!checked) {
                  setCreateMetadataInstructions('');
                }
              }}
            />
          </div>

          {autoGenerateMetadata && (
            <div className="space-y-2 px-1">
              <Label htmlFor="create-metadata-instructions" className="text-sm font-medium">
                Metadata Instructions <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="create-metadata-instructions"
                value={createMetadataInstructions}
                onChange={(e) => setCreateMetadataInstructions(e.target.value.slice(0, MAX_METADATA_INSTRUCTIONS_LENGTH))}
                placeholder="e.g., Use concise labels, emphasize product setup steps, and prefer customer-facing terminology."
                rows={3}
                maxLength={MAX_METADATA_INSTRUCTIONS_LENGTH}
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  Guides metadata wording and emphasis only. Segment boundaries come from the ranges you set above.
                </p>
                <span className="text-xs text-muted-foreground">
                  {createMetadataInstructions.length}/{MAX_METADATA_INSTRUCTIONS_LENGTH}
                </span>
              </div>
            </div>
          )}

          {/* Existing segments preview (read-only) */}
          <div className="px-1 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold">
                Video Segments
                {segments.length > 0 && (
                  <span className="text-subtle ml-1">({segments.length})</span>
                )}
              </h4>
              {(segmentsLoading || segmentationsLoading) && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
            {segments.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {segments.map((segment, idx) => {
                  const isProcessing = processingSegmentationIds.has(segment.segmentation_id || '');
                  return (
                    <div
                      key={segment.id}
                      className={`group relative text-left rounded-lg overflow-hidden transition-all cursor-default ${
                        isProcessing ? 'opacity-50' : ''
                      }`}
                    >
                      {/* Thumbnail */}
                      {segment.thumbnail_url ? (
                        <div className="aspect-video bg-zinc-800">
                          <img
                            src={segment.thumbnail_url}
                            alt={segment.label || `Segment ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-zinc-800 flex items-center justify-center">
                          <Play className="h-6 w-6 text-gray-500" />
                        </div>
                      )}

                      {/* Overlay with info */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Badge
                            variant={segment.segment_type === 'auto' ? 'secondary' : 'default'}
                            className="text-[10px] px-1 py-0 h-4"
                          >
                            {segment.segment_type}
                          </Badge>
                          {segment.label && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-black/50 border-white/30 text-white">
                              labeled
                            </Badge>
                          )}
                        </div>
                        <div className="text-white text-xs font-medium truncate">
                          {segment.label || `Segment ${idx + 1}`}
                        </div>
                        <div className="text-white/70 text-[10px]">
                          {formatTime(segment.start_time)} – {formatTime(segment.end_time)}
                        </div>
                      </div>
                      {isProcessing && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                          <Loader2 className="h-4 w-4 animate-spin text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : !segmentsLoading && !segmentationsLoading ? (
              <p className="text-xs text-subtle">No segments found for this video.</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateSegmentOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Validate all segment tabs
                const invalidTabs = segmentTabs.filter(tab => 
                  tab.end_time <= tab.start_time || tab.end_time - tab.start_time < 1
                );
                if (invalidTabs.length > 0) {
                  toast.error('All segments must have end time greater than start time and be at least 1 second long');
                  return;
                }
                if (segmentTabs.length === 0) {
                  toast.error('Please add at least one segment');
                  return;
                }
                // Submit all segment tabs
                createSegmentationMutation.mutate({
                  segments: segmentTabs.map(tab => ({
                    start_time: tab.start_time,
                    end_time: tab.end_time,
                    label: tab.label || undefined,
                  })),
                  metadataInstructions: autoGenerateMetadata
                    ? createMetadataInstructions.trim() || undefined
                    : undefined,
                });
              }}
              disabled={createSegmentationMutation.isPending || segmentTabs.length === 0}
            >
              {createSegmentationMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save {segmentTabs.length > 1 ? `${segmentTabs.length} Segments` : 'Segment'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!metadataGenerationSegmentationId}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseGenerateMetadataDialog();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Generate Metadata
            </DialogTitle>
            <DialogDescription>
              Add optional instructions to guide wording, emphasis, and terminology for AI-generated metadata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="generate-metadata-instructions" className="text-sm font-medium">
              Metadata Instructions <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="generate-metadata-instructions"
              value={metadataGenerationInstructions}
              onChange={(e) => setMetadataGenerationInstructions(e.target.value.slice(0, MAX_METADATA_INSTRUCTIONS_LENGTH))}
              placeholder="e.g., Keep descriptions concise, highlight key actions, and use implementation-focused terminology."
              rows={4}
              maxLength={MAX_METADATA_INSTRUCTIONS_LENGTH}
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                This affects generated metadata only and does not change segmentation boundaries.
              </p>
              <span className="text-xs text-muted-foreground">
                {metadataGenerationInstructions.length}/{MAX_METADATA_INSTRUCTIONS_LENGTH}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseGenerateMetadataDialog}
              disabled={generateMetadataMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitGenerateMetadata} disabled={generateMetadataMutation.isPending}>
              {generateMetadataMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                  Generate Metadata
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply-all pending review Confirmation Dialog */}
      <AlertDialog open={isBulkApplyConfirmOpen} onOpenChange={setIsBulkApplyConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply review to {pendingCandidates.length} pending segment{pendingCandidates.length === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Applies each segment's recommended action. You can still adjust decisions
                  individually afterward.
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-sm">
                  {(Object.keys(BULK_DECISION_LABELS) as PromptReviewDecision[]).map((decision) => {
                    const count = bulkDecisionBreakdown[decision];
                    if (!count) return null;
                    return (
                      <li key={decision}>
                        <span className="font-medium">{count}</span>{' '}
                        {BULK_DECISION_LABELS[decision].toLowerCase()}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applyAllPendingMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={applyAllPendingMutation.isPending || pendingCandidates.length === 0}
              onClick={(e) => {
                e.preventDefault();
                applyAllPendingMutation.mutate();
              }}
            >
              {applyAllPendingMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                'Apply all'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete All Segments Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteSegmentsDialog} onOpenChange={setShowBulkDeleteSegmentsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all segments?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all segments from this video and update the catalog.
              The video itself will be kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleteSegmentsMutation.isPending}
              onClick={() => bulkDeleteSegmentsMutation.mutate()}
            >
              {bulkDeleteSegmentsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete all segments'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Video Confirmation Dialog */}
      <AlertDialog open={showDeleteVideoDialog} onOpenChange={setShowDeleteVideoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{videoData.title || videoData.filename}"? This action cannot be undone and will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>The video file from storage</li>
                <li>All segments and segmentations</li>
                <li>All associated metadata</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteVideoMutation.mutate()}
              disabled={deleteVideoMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteVideoMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Video'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {videoId && orgId && attachedTrainingForVideo ? (
        <KnowledgeRemovalDialog
          organizationId={orgId}
          interviewerId={attachedTrainingForVideo.id}
          sourceLabel={videoData.title || videoData.filename || 'this video'}
          payload={{ selected_video_ids: [videoId] }}
          open={showTrainingRemovalDialog}
          onOpenChange={setShowTrainingRemovalDialog}
          title="Delete Video From Library"
          description={
            <>
              Delete <span className="font-medium text-foreground">{videoData.title || videoData.filename || 'this video'}</span> from the content library and remove it from your training workspace. I&apos;ll preview what knowledge may weaken before anything changes.
            </>
          }
          confirmLabel="Remove And Delete"
          onRemoved={async () => {
            await deleteVideoMutation.mutateAsync();
          }}
        />
      ) : null}

      <Dialog open={!!segmentToDelete} onOpenChange={(open) => !open && setSegmentToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Segment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this segment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {segmentToDelete && (
            <div className="py-4">
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm font-medium">
                  {segmentToDelete.label || 'Untitled Segment'}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSegmentToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (segmentToDelete) {
                  deleteSegmentMutation.mutate({
                    segmentationId: segmentToDelete.segmentation_id,
                    segmentId: segmentToDelete.id
                  });
                }
              }}
              disabled={deleteSegmentMutation.isPending}
            >
              {deleteSegmentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metadata Generation Results Modal */}
      <Dialog open={!!generatedMetadataResults} onOpenChange={(open) => !open && handleCloseResults()}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Metadata Applied Successfully
            </DialogTitle>
            <DialogDescription>
              Review the AI-generated metadata that has been applied to your segments. You can edit individual segments if needed.
            </DialogDescription>
          </DialogHeader>

          {generatedMetadataResults && (
            <div className="space-y-4 py-4">
              {generatedMetadataResults.segments && generatedMetadataResults.segments.length > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Applied metadata to {generatedMetadataResults.metadata_generated || generatedMetadataResults.segments.length} segment{generatedMetadataResults.segments.length !== 1 ? 's' : ''}
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Check className="h-3 w-3 mr-1" />
                      Saved
                    </Badge>
                  </div>
                  <div className="space-y-4">
                    {generatedMetadataResults.segments.map((segment, idx) => (
                      <div key={segment.segment_id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Segment {idx + 1}</span>
                            <span>{segment.generated_metadata?.label || 'Untitled'}</span>
                          </h4>
                          {segment.generated_metadata?.content_type && (
                            <Badge variant="outline">{segment.generated_metadata.content_type}</Badge>
                          )}
                        </div>

                        <div className="text-sm text-muted-foreground">
                          {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                        </div>

                        {segment.generated_metadata && (
                          <div className="space-y-2">
                            {segment.generated_metadata.description && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Description</Label>
                                <p className="text-sm mt-1">{segment.generated_metadata.description}</p>
                              </div>
                            )}

                            {segment.generated_metadata.ai_context && (
                              <div>
                                <Label className="text-xs text-muted-foreground">AI Context</Label>
                                <p className="text-sm mt-1 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                                  {segment.generated_metadata.ai_context}
                                </p>
                              </div>
                            )}

                            {segment.generated_metadata.tags && segment.generated_metadata.tags.length > 0 && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Tags</Label>
                                <div className="flex gap-1 flex-wrap mt-1">
                                  {segment.generated_metadata.tags.map(tag => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No metadata was generated
                </div>
              )}

              {generatedMetadataResults.errors && generatedMetadataResults.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-1">Some errors occurred:</div>
                    <ul className="list-disc list-inside text-sm">
                      {generatedMetadataResults.errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={handleCloseResults}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoViewerPage;
